import { ref, watch, type Ref } from 'vue';
import { acceptHMRUpdate, defineStore } from 'pinia';

export const enum Theme {
    Light = 'light',
    Dark = 'dark',
}

export const useA11yStore = defineStore(
    'a11yStore',
    () => {
        const fontSizeMultiplier: Ref<number> = ref(1);
        const theme: Ref<Theme> = ref(Theme.Light);
        const grayscale: Ref<boolean> = ref(false);
        const highContrast: Ref<boolean> = ref(false);
        const underlineLinks: Ref<boolean> = ref(false);

        watch(
            [
                fontSizeMultiplier,
                theme,
                grayscale,
                highContrast,
                underlineLinks,
            ],
            ([
                fontSizeMultiplier,
                theme,
                grayscale,
                highContrast,
                underlineLinks,
            ]) => {
                document.documentElement.style.setProperty(
                    '--font-size-multiplier',
                    Math.min(Math.max(fontSizeMultiplier, 0.4), 3).toString()
                );

                document.documentElement.dataset.theme = theme;
                document.documentElement.dataset.grayscale =
                    grayscale.toString();
                document.documentElement.dataset.highContrast =
                    highContrast.toString();
                document.documentElement.dataset.underlineLinks =
                    underlineLinks.toString();
            },
            {
                immediate: true,
            }
        );

        function reset() {
            fontSizeMultiplier.value = 1;
            theme.value = Theme.Light;
            grayscale.value = false;
            highContrast.value = false;
            underlineLinks.value = false;
        }

        return {
            fontSizeMultiplier,
            theme,
            grayscale,
            highContrast,
            underlineLinks,
            reset,
        };
    },
    {
        persist: true,
    }
);

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useA11yStore, import.meta.hot));
}
